// ============================================================
// TMR Portal Admin — clinic application reads and writes, over @tmr/api-client (28)
// ============================================================
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { api } from '@tmr/api-client';
import type { Paged } from '@tmr/api-client';
import { companyApplicationKeys, companyKeys } from '../queryClient';
import type { OnboardCompanyRequest } from '../companies/contract';
import type {
  ApproveCompanyApplicationResponse,
  CompanyApplicationDetail,
  CompanyApplicationFilters,
  CompanyApplicationListItem,
} from './contract';

/** Rows per page of the queue. */
export const APPLICATION_PAGE_SIZE = 20;

/**
 * One page of the queue, newest first.
 *
 * @param filters Status, search and page.
 * @param enabled Whether the operator holds `company.applications.read` — false skips a request that would 403.
 */
export function useCompanyApplications(
  filters: CompanyApplicationFilters = {},
  enabled = true,
): UseQueryResult<Paged<CompanyApplicationListItem>> {
  const query = {
    status: filters.status,
    search: filters.search,
    page: filters.page ?? 1,
    pageSize: filters.pageSize ?? APPLICATION_PAGE_SIZE,
  };

  return useQuery({
    queryKey: companyApplicationKeys.list(query),
    queryFn: ({ signal }) => api.get<Paged<CompanyApplicationListItem>>('/company-applications', { query, signal }),
    placeholderData: (previous) => previous,
    // Applications arrive from the site while the screen is open; coming back to the tab is the moment to look.
    refetchOnWindowFocus: true,
    enabled,
  });
}

/**
 * One application with everything the clinic typed.
 *
 * @param applicationId The application, or undefined to skip the request.
 */
export function useCompanyApplication(applicationId: string | undefined): UseQueryResult<CompanyApplicationDetail> {
  return useQuery({
    queryKey: companyApplicationKeys.detail(applicationId ?? ''),
    queryFn: ({ signal }) => api.get<CompanyApplicationDetail>(`/company-applications/${applicationId}`, { signal }),
    enabled: Boolean(applicationId),
  });
}

/**
 * Takes the application — it moves to "Em análise" with the operator as reviewer.
 *
 * @param applicationId The application taken.
 */
export function useReviewCompanyApplication(applicationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<unknown>(`/company-applications/${applicationId}/review`),
    onSettled: () => {
      // Settled, not only success: a 409 means somebody else closed it, and the screen should show that.
      void queryClient.invalidateQueries({ queryKey: companyApplicationKeys.all });
    },
  });
}

/**
 * Rejects the application with a reason. Final.
 *
 * @param applicationId The application rejected.
 */
export function useRejectCompanyApplication(applicationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reason: string) => api.post<unknown>(`/company-applications/${applicationId}/reject`, { reason }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: companyApplicationKeys.all });
    },
  });
}

/**
 * Approves the application by onboarding the Empresa the team reviewed — the very body of
 * `POST /companies/onboard`. Answers 202 while the export to the legacy is Pending.
 *
 * @param applicationId The application approved.
 */
export function useApproveCompanyApplication(applicationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: OnboardCompanyRequest) =>
      api.post<ApproveCompanyApplicationResponse>(`/company-applications/${applicationId}/approve`, request),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: companyApplicationKeys.all });
    },
    onSuccess: () => {
      // A new Empresa belongs on every list of Empresas.
      void queryClient.invalidateQueries({ queryKey: companyKeys.all });
    },
  });
}
